import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { exportableColumns } from 'src/pages/AdminPage/BillQueue/queue';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import { AUTO_HEIGHT_SCROLLBAR_ROOM } from 'src/utils/dataGrid';
import { answered } from 'src/components/property/UtilityPortalCredentials/asyncResult';
import { credentialFailureMessage, credentialStatusLabel, credentialStatusTooltip, credentialStatusVariant, isAutomatedCredential } from 'src/components/property/UtilityPortalCredentials/credentialStatus';
import { filterCredentials, statusOptions } from './filters';
const formatDate = value => value ? new Date(value).toLocaleDateString() : '-';

/**
 * Cross-company credential view for Livable staff. Read-and-manage only: credentials are ENTERED on
 * the property's utility, which is where they are scoped to a property utility and where the scraper
 * can act on them. This page exists so support can answer "is this company's login healthy" without
 * walking every property.
 *
 * Replaces the former UtilityCredentialPlayground, which was a prototype with an in-memory mock and
 * an environment switch that no deployed build could satisfy.
 */
export const UtilityCredentials = observer(() => {
  const store = useStore();
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    credentials = _useState2[0],
    setCredentials = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    loading = _useState6[0],
    setLoading = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    search = _useState8[0],
    setSearch = _useState8[1];
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    statusFilter = _useState0[0],
    setStatusFilter = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    providerFilter = _useState10[0],
    setProviderFilter = _useState10[1];

  // `run()` cancels the in-flight request every time it is called and the model swallows the
  // cancel, so a stale response must not paint over a newer one.
  const requestSeq = useRef(0);
  const load = async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');
    try {
      // Every company, not the one picked in the header: support searches by customer here, and the
      // header switcher silently scoped this list to a company with no logins.
      const resp = await store.admin.getUtilityCredentialsAsync.run(undefined);
      if (seq !== requestSeq.current) return; // superseded; the newer request owns the state
      const rows = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
      // The model swallows API errors and resolves undefined, so the catch below never fires on a
      // failed request. Without this check the page showed an empty grid, which reads as "this
      // company has no credentials" rather than "the request failed". A run superseded by a later
      // one resolves undefined too, with isError still false, so both have to be caught here.
      if (!answered(resp) || store.admin.getUtilityCredentialsAsync.isError) {
        var _store$admin$getUtili, _errorResp$body;
        // This page is the only user-facing surface left for this lookup now that it is silent, so
        // it takes the title and the body: validation detail lands in the body while the title stays
        // generic, and the parser uses the literal "Error" when the payload carried nothing useful.
        const errorResp = (_store$admin$getUtili = store.admin.getUtilityCredentialsAsync.error) === null || _store$admin$getUtili === void 0 ? void 0 : _store$admin$getUtili.errorResp;
        const detail = [errorResp === null || errorResp === void 0 ? void 0 : errorResp.title, ...((_errorResp$body = errorResp === null || errorResp === void 0 ? void 0 : errorResp.body) !== null && _errorResp$body !== void 0 ? _errorResp$body : [])].flat(Infinity).filter(part => typeof part === 'string' && part.trim() && part !== 'Error').join(' ');
        setError(detail || 'Could not load utility credentials.');
        setCredentials([]);
        return;
      }
      setCredentials(rows);
    } catch (ex) {
      if (seq !== requestSeq.current) return;
      setError('Could not load utility credentials.');
      setCredentials([]);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const visible = useMemo(() => filterCredentials(credentials, {
    search,
    status: statusFilter,
    providerKey: providerFilter
  }), [credentials, search, statusFilter, providerFilter]);

  // From the rows themselves rather than a fixed list: the seeded registry has 35 providers and a
  // dropdown of 35 where 4 are in use is a worse filter than one that lists the 4.
  const providerOptions = useMemo(() => [...new Set(credentials.map(c => c.providerKey).filter(Boolean))].sort(), [credentials]);
  const columns = useMemo(() => [
  // Customer and property first: support arrives here knowing who called, not which provider.
  {
    field: 'companyName',
    headerName: 'Customer',
    flex: 1,
    minWidth: 180,
    isExportable: true,
    renderCell: _ref => {
      let row = _ref.row;
      return row.companyName || '-';
    },
    valueGetterForExport: _ref2 => {
      let row = _ref2.row;
      return row.companyName || '-';
    }
  }, {
    field: 'propertyAddress',
    headerName: 'Property',
    flex: 1.2,
    minWidth: 200,
    isExportable: true,
    renderCell: _ref3 => {
      let row = _ref3.row;
      return row.propertyAddress || '-';
    },
    valueGetterForExport: _ref4 => {
      let row = _ref4.row;
      return row.propertyAddress || '-';
    }
  }, {
    // The address the utility bills, which is not always the property's own.
    field: 'serviceAddress',
    headerName: 'Service address',
    flex: 1.2,
    minWidth: 200,
    isExportable: true,
    renderCell: _ref5 => {
      let row = _ref5.row;
      return row.serviceAddress || '-';
    },
    valueGetterForExport: _ref6 => {
      let row = _ref6.row;
      return row.serviceAddress || '-';
    }
  }, {
    field: 'providerKey',
    headerName: 'Provider',
    flex: 1,
    minWidth: 160,
    isExportable: true
  },
  // The masked hint only. The password is never returned by any endpoint this page calls.
  {
    field: 'usernameHint',
    headerName: 'Username',
    flex: 1,
    minWidth: 180,
    isExportable: true
  }, {
    field: 'accountReference',
    headerName: 'Account',
    flex: 1,
    minWidth: 140,
    isExportable: true,
    renderCell: _ref7 => {
      let row = _ref7.row;
      return row.accountReference || '-';
    },
    valueGetterForExport: _ref8 => {
      let row = _ref8.row;
      return row.accountReference || '-';
    }
  }, {
    field: 'status',
    headerName: 'Status',
    width: 200,
    isExportable: true,
    renderCell: _ref9 => {
      let row = _ref9.row;
      return /*#__PURE__*/React.createElement(Stack, {
        sx: {
          paddingY: '6px'
        }
      }, /*#__PURE__*/React.createElement(Tooltip, {
        title: credentialStatusTooltip(row, {
          isAutomated: isAutomatedCredential(row)
        }),
        placement: "top",
        arrow: true
      }, /*#__PURE__*/React.createElement(Chip, {
        tabIndex: 0,
        size: "small",
        label: credentialStatusLabel(row),
        variant: credentialStatusVariant(row)
      })), credentialFailureMessage(row) ? /*#__PURE__*/React.createElement(Typography, {
        variant: "caption"
      }, credentialFailureMessage(row)) : null);
    },
    valueGetterForExport: _ref0 => {
      let row = _ref0.row;
      return credentialStatusLabel(row);
    }
  }, {
    field: 'secretVersion',
    headerName: 'Version',
    width: 100,
    type: 'number',
    isExportable: true
  }, {
    field: 'lastVerifiedAt',
    headerName: 'Last verified',
    width: 140,
    isExportable: true,
    renderCell: _ref1 => {
      let row = _ref1.row;
      return formatDate(row.lastVerifiedAt);
    },
    valueGetterForExport: _ref10 => {
      let row = _ref10.row;
      return formatDate(row.lastVerifiedAt);
    }
  }], []);
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, "Utility portal credentials"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginY: '8px'
    }
  }, "Credentials are added on a property's utility, so they are scoped to the utility the scraper needs them for. This list is read-only: it shows what is stored and whether it is healthy. Passwords are encrypted and are never displayed."), error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error"
  }, error) : null, /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px',
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 2,
    sx: {
      marginBottom: '16px'
    },
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Search customer, property, address, provider, account",
    value: search,
    onChange: e => setSearch(e.target.value),
    sx: {
      minWidth: 340
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "Status",
    value: statusFilter,
    onChange: e => setStatusFilter(e.target.value),
    sx: {
      minWidth: 160
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "Any status"), statusOptions(credentials).map(label => /*#__PURE__*/React.createElement(MenuItem, {
    key: label,
    value: label
  }, label))), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: "Provider",
    value: providerFilter,
    onChange: e => setProviderFilter(e.target.value),
    sx: {
      minWidth: 160
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "Any provider"), providerOptions.map(key => /*#__PURE__*/React.createElement(MenuItem, {
    key: key,
    value: key
  }, key))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      flexGrow: 1
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => exportCSVFromDataGrid({
      columns: exportableColumns(columns),
      rows: visible,
      name: 'utility-credentials.csv'
    })
  }, "Export CSV")), /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    sx: AUTO_HEIGHT_SCROLLBAR_ROOM,
    columns: columns,
    rows: visible,
    loading: loading,
    getRowId: row => row.id,
    getRowHeight: () => 'auto',
    disableRowSelectionOnClick: true,
    initialState: {
      pagination: {
        paginationModel: {
          page: 0,
          pageSize: 25
        }
      }
    },
    pageSizeOptions: [25, 50, 100]
  }), !visible.length && !loading && !error ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginTop: '12px'
    }
  }, "No credentials stored.") : null));
});